import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errorMessage } from "../api/client";

export type Notification = { id: string; kind: string; title: string; body: string; url: string; importance: "info" | "action" | "critical"; data: Record<string, unknown>; read_at: string | null; created_at: string };
export type Pref = { kind: string; label: string; group: string; critical: boolean; in_app: boolean; email: boolean; digest: boolean };

async function unwrap<T>(p: Promise<{ data?: unknown; error?: unknown }>): Promise<T> {
  const { data, error } = await p;
  if (error) throw new Error(errorMessage(error));
  return data as T;
}

export function useUnreadCount(enabled = true) {
  return useQuery({ queryKey: ["notifications", "unread"], enabled, refetchInterval: 30_000, queryFn: () => unwrap<{ count: number }>(api.GET("/api/v1/workflow/notifications/unread/")) });
}
export function useNotifications(unreadOnly = false) {
  return useQuery({ queryKey: ["notifications", "list", unreadOnly], queryFn: () => unwrap<{ count: number; results: Notification[] }>(api.GET("/api/v1/workflow/notifications/", { params: { query: { page_size: 50, ...(unreadOnly ? { unread: "1" } : {}) } as never } })) });
}
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string | "all") => {
      if (id === "all") await unwrap(api.POST("/api/v1/workflow/notifications/read-all/"));
      else await unwrap(api.POST("/api/v1/workflow/notifications/{id}/read/", { params: { path: { id } } }));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}
export function usePreferences() {
  return useQuery({ queryKey: ["notification-preferences"], queryFn: () => unwrap<Pref[]>(api.GET("/api/v1/workflow/notification-preferences/")) });
}
export function useSavePreferences() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (items: Partial<Pref>[]) => unwrap<Pref[]>(api.PUT("/api/v1/workflow/notification-preferences/", { body: items as never })), onSuccess: (d) => qc.setQueryData(["notification-preferences"], d) });
}
